import { Component, inject, OnInit, signal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
    CapabilityLifecycleControllerService,
    DecommissionDryRunResult,
    ScheduleAction,
    ScheduleRef,
    TaskRef,
} from '../../api/capability-service';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';

export interface CapabilityDecommissionDialogData {
    code: string;
    version: string;
}

/**
 * Phase 2b — decommission dry-run + per-schedule action picker.
 *
 * <p>Opens via the detail page's "Decommission" toolbar button when the
 * capability is INACTIVE. Fires the dry-run endpoint to fetch dependents,
 * lets the operator pick DEACTIVATE / DELETE per schedule, then closes with
 * the action list as its result. Parent calls
 * {@code lifecycleService.decommissionConfirm(...)} with the returned array.
 *
 * <p>Phase 1c ships with a stubbed scheduler-service client that returns
 * empty dependents — until Phase 1e wires the real HTTP, the dependents
 * panels will always be empty, and the operator can just confirm.
 */
@Component({
    selector: 'app-capability-decommission-dialog',
    standalone: false,
    template: `
<div class="bw-dialog cap-decom-dialog">
    <lib-page-header screenName="Decommission" title="Decommission capability" backTo="">
        <mat-icon icon>block</mat-icon>
        <ng-container subtitle>
            <span class="mono">{{ data.code }} {{ data.version }}</span>
        </ng-container>
        <ng-container actions>
            <button mat-icon-button (click)="cancel()" matTooltip="Cancel" aria-label="Cancel">
                <mat-icon>close</mat-icon>
            </button>
        </ng-container>
    </lib-page-header>

    <div class="bw-body">
        <div class="info-block">
            <mat-icon class="info-icon">warning</mat-icon>
            <div>
                <p><strong>Decommissioning is irreversible.</strong> Existing task refs are wiped; the
                capability disappears from the schedule-creation wizard's chip palette permanently.</p>
                <p *ngIf="loading()">Looking up dependent schedules…</p>
            </div>
        </div>

        <mat-progress-bar mode="indeterminate" *ngIf="loading()" aria-label="Loading"></mat-progress-bar>

        <ng-container *ngIf="dryRun() as r">
            <h3 class="section-title">
                <mat-icon>schedule</mat-icon>
                Dependent schedules ({{ r.dependentSchedules?.length || 0 }})
            </h3>

            <div class="empty-inline" *ngIf="!r.dependentSchedules?.length">
                <mat-icon>check_circle</mat-icon>
                No schedules currently pin to this capability version. Safe to decommission.
            </div>

            <table mat-table [dataSource]="r.dependentSchedules || []" class="data-table dep-table"
                   *ngIf="r.dependentSchedules?.length">
                <ng-container matColumnDef="scheduleName">
                    <th mat-header-cell *matHeaderCellDef>Schedule</th>
                    <td mat-cell *matCellDef="let s">
                        <span class="mono">{{ s.scheduleName }}</span>
                        <span class="sched-id">({{ s.scheduleId }})</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="owner">
                    <th mat-header-cell *matHeaderCellDef>Owner</th>
                    <td mat-cell *matCellDef="let s">{{ s.owner }}</td>
                </ng-container>
                <ng-container matColumnDef="lastFiredAt">
                    <th mat-header-cell *matHeaderCellDef>Last fired</th>
                    <td mat-cell *matCellDef="let s">
                        <span *ngIf="s.lastFiredAt">{{ s.lastFiredAt | date:'medium' }}</span>
                        <span *ngIf="!s.lastFiredAt" class="muted">never</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="action">
                    <th mat-header-cell *matHeaderCellDef>Action on decommission</th>
                    <td mat-cell *matCellDef="let s">
                        <mat-button-toggle-group [value]="actionFor(s.scheduleId)"
                                                 (change)="setAction(s.scheduleId, $event.value)">
                            <mat-button-toggle value="DEACTIVATE">Deactivate</mat-button-toggle>
                            <mat-button-toggle value="DELETE">Delete</mat-button-toggle>
                        </mat-button-toggle-group>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="scheduleColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: scheduleColumns"></tr>
            </table>

            <h3 class="section-title">
                <mat-icon>assignment</mat-icon>
                Dependent tasks ({{ r.dependentTasks?.length || 0 }})
            </h3>

            <div class="empty-inline" *ngIf="!r.dependentTasks?.length">
                <mat-icon>check_circle</mat-icon>
                No tasks currently reference this capability version.
            </div>

            <table mat-table [dataSource]="r.dependentTasks || []" class="data-table dep-table"
                   *ngIf="r.dependentTasks?.length">
                <ng-container matColumnDef="taskId">
                    <th mat-header-cell *matHeaderCellDef>Task</th>
                    <td mat-cell *matCellDef="let t"><span class="mono">{{ t.taskId }}</span></td>
                </ng-container>
                <ng-container matColumnDef="runnerName">
                    <th mat-header-cell *matHeaderCellDef>Runner</th>
                    <td mat-cell *matCellDef="let t">
                        <span *ngIf="t.runnerName">{{ t.runnerName }}</span>
                        <span *ngIf="!t.runnerName" class="muted">—</span>
                    </td>
                </ng-container>
                <ng-container matColumnDef="lastSeenAt">
                    <th mat-header-cell *matHeaderCellDef>Last seen</th>
                    <td mat-cell *matCellDef="let t">
                        <span *ngIf="t.lastSeenAt">{{ t.lastSeenAt | date:'medium' }}</span>
                        <span *ngIf="!t.lastSeenAt" class="muted">—</span>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="taskColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: taskColumns"></tr>
            </table>
            <p class="muted task-note" *ngIf="r.dependentTasks?.length">
                Task refs are wiped automatically on decommission. The runner-side state lookup
                (runner name / last-seen) is read-only — Phase 1e will populate it from runner-service.
            </p>
        </ng-container>
    </div>

    <div class="bw-footer">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-flat-button color="warn" [disabled]="loading()"
                (click)="confirm()">
            Decommission
        </button>
    </div>
</div>
`,
    styles: [`
.bw-body { padding: 16px 24px; max-height: 70vh; overflow: auto; }
.bw-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 24px; border-top: 1px solid var(--border-divider); }
.info-block { display: flex; gap: 12px; padding: 12px; background: var(--warning-bg); border-radius: 8px; margin-bottom: 16px; }
.info-block p { margin: 0 0 6px 0; font-size: 13px; color: var(--text-primary); }
.info-icon { color: var(--warning); flex-shrink: 0; }
.section-title { display: flex; align-items: center; gap: 6px; margin: 16px 0 8px; font-size: 13px; font-weight: 600; color: var(--text-secondary); }
.section-title mat-icon { font-size: 18px; width: 18px; height: 18px; color: var(--accent); }
.empty-inline { display: flex; align-items: center; gap: 8px; padding: 8px 12px; color: var(--text-tertiary); font-size: 12px; }
.empty-inline mat-icon { color: var(--success); font-size: 18px; width: 18px; height: 18px; }
.dep-table { width: 100%; }
.sched-id { color: var(--text-tertiary); font-size: 11px; margin-left: 6px; }
.task-note { font-size: 11px; padding: 4px 12px; }
.muted { color: var(--text-tertiary); }
.mono { font-family: var(--font-mono, monospace); font-size: 12px; }
`],
})
export class CapabilityDecommissionDialogComponent implements OnInit {

    data: CapabilityDecommissionDialogData = inject(MAT_DIALOG_DATA);
    private ref: MatDialogRef<CapabilityDecommissionDialogComponent> = inject(MatDialogRef);
    private lifecycleService: CapabilityLifecycleControllerService = inject(CapabilityLifecycleControllerService);
    private permissionChecker: PermissionCheckerService = inject(PermissionCheckerService);

    loading = signal<boolean>(true);
    dryRun  = signal<DecommissionDryRunResult | null>(null);
    actions = new Map<string, ScheduleAction.ActionEnum>();

    scheduleColumns = ['scheduleName', 'owner', 'lastFiredAt', 'action'];
    taskColumns     = ['taskId', 'runnerName', 'lastSeenAt'];

    ngOnInit(): void {
        this.lifecycleService.decommissionDryRun(this.data.code, this.data.version).subscribe({
            next: (r) => {
                this.dryRun.set(r);
                // Default action: DEACTIVATE (less destructive than DELETE).
                (r.dependentSchedules ?? []).forEach((s: ScheduleRef) => {
                    if (s.scheduleId) this.actions.set(s.scheduleId, ScheduleAction.ActionEnum.Deactivate);
                });
                this.loading.set(false);
            },
            error: (e: any) => {
                this.loading.set(false);
                alert(`Dry-run failed: ${typeof e === 'string' ? e : JSON.stringify(e)}`);
                this.ref.close(null);
            },
        });
    }

    actionFor(scheduleId: string): ScheduleAction.ActionEnum {
        return this.actions.get(scheduleId) ?? ScheduleAction.ActionEnum.Deactivate;
    }

    setAction(scheduleId: string, value: ScheduleAction.ActionEnum): void {
        this.actions.set(scheduleId, value);
    }

    cancel(): void { this.ref.close(null); }

    confirm(): void {
        if (!this.permissionChecker.hasAccessToControlCapability()) { this.ref.close(null); return; }
        const out: ScheduleAction[] = [];
        for (const [scheduleId, action] of this.actions.entries()) {
            out.push({ scheduleId, action });
        }
        this.ref.close(out);
    }
}
